import { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Card, CardHeader, Btn, Input, Select, Table, fmt, Badge, PageHeader, POINT_VALUES } from '../components/ui';
import { Plus, Calculator, RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';

const INIT = {
  customer_id: '', script_id: '', linked_buy_id: '',
  trade_date: new Date().toISOString().split('T')[0],
  lot_size: '', quantity: '', point_value: '0.10', price: '', remarks: ''
};

export default function SellPage() {
  const [form, setForm] = useState(INIT);
  const [customers, setCustomers] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [openBuys, setOpenBuys] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get('/masters/customers').then(r => setCustomers(r.data));
    api.get('/masters/scripts').then(r => setScripts(r.data));
    load();
  }, []);

  const load = () => api.get('/transactions', { params: { transaction_type: 'SELL' } }).then(r => setTransactions(r.data));

  // Auto-fill lot size from script
  const handleScriptChange = (e) => {
    const id = e.target.value;
    const script = scripts.find(s => s.id === parseInt(id));
    setForm(f => ({ ...f, script_id: id, lot_size: script ? script.lot_size : '', linked_buy_id: '' }));
    setOpenBuys([]);
  };

  // Load open buys when customer + script selected
  useEffect(() => {
    if (form.customer_id && form.script_id) {
      api.get('/transactions/open-buys', { params: { customer_id: form.customer_id, script_id: form.script_id } })
        .then(r => setOpenBuys(r.data)).catch(() => setOpenBuys([]));
    } else { setOpenBuys([]); }
  }, [form.customer_id, form.script_id]);

  // Live preview
  useEffect(() => {
    const { customer_id, quantity, price, point_value, lot_size, linked_buy_id } = form;
    if (customer_id && quantity && price && point_value) {
      const cust = customers.find(c => c.id === parseInt(customer_id));
      const brokerageAmt = parseFloat(cust?.brokerage_amount || 0);
      const qty = parseInt(quantity) || 0;
      const pv = parseFloat(point_value) || 0;
      const pr = parseFloat(price) || 0;
      const ls = parseInt(lot_size) || 1;
      const grossAmount = qty * pr * pv;
      const netProceeds = grossAmount - brokerageAmt;
      const totalLots = qty / ls;

      let costOfAcq = null, profitLoss = null;
      if (linked_buy_id) {
        const buy = openBuys.find(b => b.id === parseInt(linked_buy_id));
        if (buy) {
          costOfAcq = parseFloat(buy.net_amount);
          profitLoss = netProceeds - costOfAcq;
        }
      }

      setPreview({ grossAmount, brokerageAmt, netProceeds, totalLots, costOfAcq, profitLoss, brokerPlan: cust?.plan_name });
    } else { setPreview(null); }
  }, [form, customers, openBuys]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.script_id || !form.quantity || !form.price) {
      toast.error('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      await api.post('/transactions', {
        ...form, transaction_type: 'SELL',
        quantity: parseInt(form.quantity), price: parseFloat(form.price), lot_size: parseInt(form.lot_size),
        linked_buy_id: form.linked_buy_id || null
      });
      toast.success('Sell entry recorded!');
      setForm(INIT); setShowForm(false); setPreview(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  };

  const selectedScript = scripts.find(s => s.id === parseInt(form.script_id));
  const pointValueOptions = selectedScript?.point_values || POINT_VALUES;

  return (
    <div>
      <PageHeader
        title="Sell Entry"
        subtitle="Record sell transactions with P&L"
        action={<Btn variant="danger" onClick={() => setShowForm(v => !v)}><Plus size={15} /> New Sell</Btn>}
      />

      {showForm && (
        <Card style={{ marginBottom: 22 }}>
          <CardHeader title="New Sell Transaction" subtitle="Link to a buy transaction to calculate P&L automatically" />
          <form onSubmit={submit} style={{ padding: 22 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 16 }}>
              <Select label="Customer *" value={form.customer_id} onChange={set('customer_id')}>
                <option value="">Select Customer</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.customer_code} — {c.customer_name}</option>)}
              </Select>
              <Select label="Script *" value={form.script_id} onChange={handleScriptChange}>
                <option value="">Select Script</option>
                {scripts.map(s => <option key={s.id} value={s.id}>{s.script_name} (Lot: {s.lot_size})</option>)}
              </Select>
              <Select label="Link to Buy (for P&L)" value={form.linked_buy_id} onChange={set('linked_buy_id')}>
                <option value="">No link (standalone sell)</option>
                {openBuys.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.transaction_no} | Qty: {b.quantity} | Net: ₹{parseFloat(b.net_amount).toFixed(2)} | {b.trade_date?.split('T')[0]}
                  </option>
                ))}
              </Select>
              <Input label="Trade Date *" type="date" value={form.trade_date} onChange={set('trade_date')} />
              <Input label="Lot Size" type="number" min="1" value={form.lot_size} onChange={set('lot_size')} placeholder="Auto-filled" />
              <Input label="Quantity *" type="number" min="1" value={form.quantity} onChange={set('quantity')} placeholder="e.g. 100" />

              {/* Point Value Picker */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Point Value *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {pointValueOptions.map(pv => (
                    <button key={pv} type="button" onClick={() => setForm(f => ({ ...f, point_value: pv }))}
                      style={{
                        flex: 1, padding: '9px 4px', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', transition: 'all 0.15s',
                        background: form.point_value === pv ? 'linear-gradient(135deg,#ef4444,#dc2626)' : '#f8fafc',
                        color: form.point_value === pv ? '#fff' : '#374151',
                        border: form.point_value === pv ? 'none' : '1px solid #e2e8f0',
                        boxShadow: form.point_value === pv ? '0 2px 8px rgba(220,38,38,0.3)' : 'none'
                      }}>
                      {pv}
                    </button>
                  ))}
                </div>
              </div>

              <Input label="Sell Price (₹) *" type="number" step="0.01" min="0.01" value={form.price} onChange={set('price')} placeholder="e.g. 280.00" />
              <Input label="Remarks" value={form.remarks} onChange={set('remarks')} placeholder="Optional" />
            </div>

            {/* Live Preview */}
            {preview && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 12, padding: 18, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
                  <Calculator size={15} color="#dc2626" />
                  <strong style={{ fontSize: 13, color: '#7f1d1d' }}>Sell Preview</strong>
                  {preview.brokerPlan && <Badge color="red">{preview.brokerPlan}</Badge>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 12 }}>
                  {[
                    ['Total Lots', parseFloat(preview.totalLots).toFixed(4)],
                    ['Gross Amount', fmt(preview.grossAmount)],
                    ['Brokerage', fmt(preview.brokerageAmt)],
                    ['Net Proceeds', fmt(preview.netProceeds)],
                  ].map(([label, val]) => (
                    <div key={label} style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', border: '1px solid #fca5a5' }}>
                      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3, fontWeight: 600 }}>{label}</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{val}</div>
                    </div>
                  ))}
                </div>

                {/* P&L Box */}
                {preview.profitLoss !== null && (
                  <div style={{
                    padding: '14px 18px', borderRadius: 10,
                    background: preview.profitLoss >= 0 ? 'linear-gradient(135deg,#10b981,#059669)' : 'linear-gradient(135deg,#ef4444,#dc2626)'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, alignItems: 'center' }}>
                      <div>
                        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginBottom: 4, fontWeight: 600 }}>COST OF ACQUISITION</div>
                        <div style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>{fmt(preview.costOfAcq)}</div>
                      </div>
                      <div>
                        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginBottom: 4, fontWeight: 600 }}>NET PROCEEDS</div>
                        <div style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>{fmt(preview.netProceeds)}</div>
                      </div>
                      <div>
                        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginBottom: 4, fontWeight: 600 }}>NET PROFIT / LOSS</div>
                        <div style={{ color: '#fff', fontWeight: 900, fontSize: 22, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {preview.profitLoss >= 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
                          {fmt(preview.profitLoss)}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>
                          {preview.costOfAcq > 0 ? ((preview.profitLoss / preview.costOfAcq) * 100).toFixed(2) + '%' : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <Btn type="submit" variant="danger" disabled={loading}>{loading ? 'Saving…' : 'Record Sell'}</Btn>
              <Btn variant="ghost" onClick={() => { setShowForm(false); setForm(INIT); setPreview(null); }}>Cancel</Btn>
            </div>
          </form>
        </Card>
      )}

      <Card>
        <CardHeader title="Sell Transactions" subtitle={`${transactions.length} records`}
          action={<Btn variant="ghost" size="sm" onClick={load}><RefreshCw size={13} /></Btn>} />
        <Table
          headers={['Txn No', 'Date', 'Customer', 'Script', 'Lots', 'Qty', 'Point Val', 'Price', 'Gross', 'Brokerage', 'Net Proceeds', 'P&L']}
          rows={transactions.map(t => {
            const pl = parseFloat(t.profit_loss || 0);
            const hasPL = t.profit_loss !== null;
            return [
              <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#64748b' }}>{t.transaction_no}</span>,
              t.trade_date?.split('T')[0],
              <div><div style={{ fontWeight: 700 }}>{t.customer_code}</div><div style={{ fontSize: 11, color: '#64748b' }}>{t.customer_name}</div></div>,
              t.script_name,
              <Badge color="orange">{parseFloat(t.total_lots || 0).toFixed(2)}</Badge>,
              t.quantity,
              <Badge color="purple">{t.point_value}</Badge>,
              fmt(t.price),
              fmt(t.gross_amount),
              fmt(t.brokerage_amount),
              <strong style={{ color: '#dc2626' }}>{fmt(t.net_amount)}</strong>,
              hasPL
                ? <strong style={{ color: pl >= 0 ? '#059669' : '#dc2626' }}>{fmt(pl)}</strong>
                : <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>
            ];
          })}
          empty="No sell transactions yet."
        />
      </Card>
    </div>
  );
}
